import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { Trophy, RefreshCw, Star, Lightbulb, AlertTriangle, Loader2 } from "lucide-react";
import { API, authHeaders } from "./shared";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TONE = {
  star: { Icon: Star, cls: "text-[#F2B705] border-[#F2B705]/40 bg-[#F2B705]/[0.07]" },
  warn: { Icon: Lightbulb, cls: "text-sky-300 border-sky-400/30 bg-sky-400/[0.06]" },
  alert: { Icon: AlertTriangle, cls: "text-red-300 border-red-400/40 bg-red-400/[0.07]" },
};

const UtilBar = ({ pct }) => (
  <div className="flex items-center gap-2 min-w-[110px]">
    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className={`h-full rounded-full ${pct >= 70 ? "bg-emerald-400" : pct >= 60 ? "bg-[#F2B705]" : "bg-red-400"}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
    <span className={`text-xs font-semibold ${pct >= 70 ? "text-emerald-400" : pct >= 60 ? "text-[#F2B705]" : "text-red-300"}`}>{pct}%</span>
  </div>
);

const StatCard = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/10 bg-[#0C0C0C] p-5">
    <div className="text-[11px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-display font-black text-2xl text-white mt-1.5">{value}</div>
    {sub && <div className="text-white/40 text-xs mt-1">{sub}</div>}
  </div>
);

export default function FleetLeaderboardModule({ token }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/fleet/leaderboard`, authHeaders(token)).then((r) => setD(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line

  if (loading && !d) return <div className="flex items-center gap-2 text-white/50 py-10"><Loader2 className="animate-spin" size={18} /> Loading fleet performance…</div>;
  if (!d) return <div className="text-white/50 py-10">Could not load the leaderboard.</div>;

  const top = d.fleet.find((r) => r.vehicle_id === d.top_vehicle_id);

  return (
    <div data-testid="fleet-leaderboard" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Trophy size={18} className="text-[#F2B705]" /> Fleet Revenue Leaderboard</h3>
          <p className="text-white/45 text-sm">Revenue, utilization ({d.window_days}-day window) and rule-based recommendations per vehicle.</p>
        </div>
        <button data-testid="leaderboard-refresh" onClick={load} className="flex items-center gap-1.5 text-sm border border-white/15 text-white/70 hover:text-white px-3 py-1.5 rounded-md">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="leaderboard-lifetime" label="Fleet Lifetime Billed Revenue" value={money(d.totals.fleet_lifetime)} />
        <StatCard testid="leaderboard-month" label={`Billed — ${d.month} (by pickup date)`} value={money(d.totals.fleet_month)} />
        <StatCard testid="leaderboard-utilization" label="Avg Utilization" value={`${d.totals.avg_utilization}%`} sub={`last ${d.window_days} days`} />
        <StatCard testid="leaderboard-top" label="Top Performer" value={top ? top.vehicle : "—"} sub={top ? `${money(top.lifetime_revenue)} lifetime` : ""} />
      </div>

      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-[#0C0C0C]">
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Vehicle</th>
              <th className="px-4 py-3 text-right">Lifetime</th>
              <th className="px-4 py-3 text-right">This Month</th>
              <th className="px-4 py-3">Utilization</th>
              <th className="px-4 py-3 text-right">Avg Length</th>
              <th className="px-4 py-3 text-right">Idle Days</th>
              <th className="px-4 py-3 text-right">Maint. Cost</th>
            </tr>
          </thead>
          <tbody>
            {d.fleet.map((r) => {
              const isTop = r.vehicle_id === d.top_vehicle_id;
              const isBottom = r.vehicle_id === d.bottom_vehicle_id;
              return (
                <Fragment key={r.vehicle_id}>
                  <tr data-testid={`leaderboard-row-${r.vehicle_id}`}
                    className={`border-b border-white/5 ${isTop ? "bg-[#F2B705]/[0.05]" : isBottom ? "bg-red-500/[0.04]" : ""}`}>
                    <td className="px-4 py-3 font-display font-black text-white/60">{isTop ? <Trophy size={15} className="text-[#F2B705]" /> : r.rank}</td>
                    <td className="px-4 py-3 font-semibold text-white">
                      {r.vehicle}
                      <span className="block text-[11px] font-normal text-white/35">{r.rentals} rental{r.rentals !== 1 ? "s" : ""}{r.currently_rented ? " · on rent now" : ""}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gold-gradient font-display">{money(r.lifetime_revenue)}</td>
                    <td className="px-4 py-3 text-right text-white/80">{money(r.month_revenue)}</td>
                    <td className="px-4 py-3"><UtilBar pct={r.utilization_pct} /></td>
                    <td className="px-4 py-3 text-right text-white/70">{r.avg_rental_days ? `${r.avg_rental_days}d` : "—"}</td>
                    <td className={`px-4 py-3 text-right ${r.idle_days !== null && r.idle_days > 10 ? "text-red-300 font-semibold" : "text-white/70"}`}>{r.idle_days === null ? "never rented" : `${r.idle_days}d`}</td>
                    <td className={`px-4 py-3 text-right ${r.maintenance_cost > 0 ? "text-white/70" : "text-white/30"}`}>{money(r.maintenance_cost)}</td>
                  </tr>
                  {r.recommendations.length > 0 && (
                    <tr className={`border-b border-white/5 ${isTop ? "bg-[#F2B705]/[0.05]" : isBottom ? "bg-red-500/[0.04]" : ""}`}>
                      <td />
                      <td colSpan={7} className="px-4 pb-3 pt-0">
                        <div className="flex flex-wrap gap-1.5" data-testid={`leaderboard-recs-${r.vehicle_id}`}>
                          {r.recommendations.map((rec) => {
                            const t = TONE[rec.tone] || TONE.warn;
                            return (
                              <span key={rec.key} className={`inline-flex items-start gap-1.5 border rounded-md px-2.5 py-1 text-[12px] leading-snug ${t.cls}`}>
                                <t.Icon size={12} className="mt-0.5 shrink-0" /> {rec.text}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-xs">Recommendations are generated by business rules (utilization, idle time, revenue vs. maintenance). AI-powered recommendations can be enabled later without changing this dashboard.</p>
    </div>
  );
}
